/**
 * Who can see and edit a skill. Mirrors the backend `SkillAccessibility` enum:
 * - `"private"`: only the author can see, edit, or invoke it.
 * - `"org"`: visible to every org member; author-only to edit.
 * - `"org-editable"`: visible to every org member, and editable by any member
 *   who has subscribed to it. Changing accessibility and deleting the whole
 *   skill stay author-only.
 */
export type SkillAccessibility = "private" | "org" | "org-editable";

/**
 * Pattern that ``Skill.create()`` and ``UpdateSkillMetadataRequest.name`` enforce
 * server-side. Mirrors ``SKILL_NAME_PATTERN`` in the Python SDK exactly. Spaces
 * are deliberately excluded so a name is unambiguously bounded inside the chat
 * composer's ``/slug`` syntax.
 */
export const SKILL_NAME_PATTERN = /^[A-Za-z0-9_-]+$/;
export const SKILL_NAME_MAX_LENGTH = 120;

/**
 * Max length of a skill version's ``description`` (the "when to use" text),
 * matching the server-side ``description`` column limit. Shared so the chat
 * tool's validation and the editor's input cap can't drift apart.
 */
export const SKILL_DESCRIPTION_MAX_LENGTH = 500;

/**
 * Validate a candidate skill name against the create / rename rules. Returns a
 * user-facing error message when invalid, or ``null`` when the name is
 * acceptable. Trim ``name`` before calling — leading / trailing whitespace
 * counts as a violation under the no-spaces rule.
 */
export function validateSkillName(name: string): string | null {
  if (name.length === 0) {
    return "Name is required.";
  }
  if (name.length > SKILL_NAME_MAX_LENGTH) {
    return `Name must be ≤ ${SKILL_NAME_MAX_LENGTH} characters.`;
  }
  if (/\s/.test(name)) {
    return "Name cannot contain spaces — use hyphens or underscores instead.";
  }
  if (!SKILL_NAME_PATTERN.test(name)) {
    return "Name can only contain letters, digits, hyphens, and underscores.";
  }
  return null;
}

/**
 * Validate a skill's ``description`` (when-to-use text) against the create /
 * edit rules. Returns a user-facing error message when invalid, or ``null``
 * when acceptable. Trim ``description`` before calling — a value that is only
 * whitespace counts as empty.
 */
export function validateSkillDescription(description: string): string | null {
  if (description.length === 0) {
    return "Description is required.";
  }
  if (description.length > SKILL_DESCRIPTION_MAX_LENGTH) {
    return `Description must be ≤ ${SKILL_DESCRIPTION_MAX_LENGTH} characters.`;
  }
  return null;
}

/**
 * Caller-relative slice of the visible skills, mirrors the backend's
 * ``SkillListScope`` enum:
 * - ``"personal"``: skills the caller authored or subscribed to.
 * - ``"org"``: org-shared skills the caller did not author (regardless of
 *   subscription state).
 */
export type SkillListScope = "personal" | "org";

export interface SkillRecord {
  skill_id: string;
  org_id: string;
  accessibility: SkillAccessibility;
  name: string;
  /** Free-form labels for categorization; edited via put_tags / remove_tags. */
  tags: string[];
  created: string;
  created_by: string;
  modified: string;
}

export interface SkillVersionRecord {
  skill_id: string;
  version: number;
  description: string;
  body: string;
  /**
   * Topic names this version's procedure investigates (e.g. `/imu/data`).
   * Extracted client-side from the body's `/topic` references. When the chat
   * AI loads the skill with a dataset in scope it resolves these to topic
   * schemas in the same tool result. Version-scoped content, not skill
   * metadata.
   */
  relevant_topics: string[];
  created: string;
  modified: string;
}

/**
 * Per-user state for a single skill.
 *
 * Exists for any skill the user has authored or explicitly subscribed to. The
 * nullable `ai_version` pins which version (if any) the user has exposed to the
 * AI's auto-invoke registry. `null` means subscribed but AI-disabled; manual
 * chip-invocation still works.
 */
export interface SkillSubscriptionRecord {
  user_id: string;
  skill_id: string;
  ai_version: number | null;
  subscribed: string;
}

export interface SkillSummary {
  skill: SkillRecord;
  latest_version: SkillVersionRecord | null;
  /** Caller's subscription row, if any. `null` means the skill is in the Org tab. */
  subscription: SkillSubscriptionRecord | null;
}

export interface CreateSkillRequest {
  name: string;
  accessibility: SkillAccessibility;
  description: string;
  body: string;
  /** Initial set of tags. Omit or pass [] to start untagged. */
  tags?: string[];
  /**
   * Topic names v1's procedure investigates. Omit or pass [] for none. See
   * {@link SkillVersionRecord.relevant_topics}.
   */
  relevant_topics?: string[];
}

export interface CreateSkillVersionRequest {
  description: string;
  body: string;
  /** Topic names this version investigates. Omit or pass [] for none. */
  relevant_topics?: string[];
}

export interface UpdateSkillVersionRequest {
  description?: string;
  body?: string;
  /**
   * New topic-name list. Omit to leave unchanged; pass [] to clear. See
   * {@link SkillVersionRecord.relevant_topics}.
   */
  relevant_topics?: string[];
}

export interface UpdateSkillMetadataRequest {
  name?: string;
  accessibility?: SkillAccessibility;
  /** Tags to add. Existing values are merged + deduped server-side. */
  put_tags?: string[];
  /** Tags to remove. Missing values are silently ignored. */
  remove_tags?: string[];
}

export interface SetSkillSubscriptionRequest {
  /** Pinned AI-available version. `null` means subscribed but AI-disabled. */
  ai_version: number | null;
}

/**
 * Whether `userId` may edit the given skill's *content* — its version bodies /
 * descriptions, the addition or deletion of versions, and the skill's name and
 * tags. True for the author of any skill, and for any member who has
 * subscribed to an `org-editable` skill.
 *
 * This deliberately does NOT cover the two ownership-level mutations —
 * changing accessibility and deleting the whole skill — which stay author-only
 * even on an `org-editable` skill. Gate those on `created_by === userId`
 * directly.
 */
export function canEditSkill(summary: SkillSummary, userId: string): boolean {
  if (summary.skill.created_by === userId) {
    return true;
  }
  return (
    summary.skill.accessibility === "org-editable" &&
    summary.subscription !== null
  );
}

/**
 * Which tab the Skills page lists `summary` under for `userId`, mirroring the
 * backend `scope` partition (see `docs/guides/ai-agents/skills.md`):
 *
 * - `"personal"`: skills the caller authored OR has subscribed to.
 * - `"org"`: org-shared skills the caller did NOT author.
 *
 * Authorship is the half a bare `subscription !== null` check misses: an
 * author who unsubscribed from their own org skill still belongs in Personal,
 * because the Org scope excludes self-authored skills. Inferring the tab from
 * subscription alone would point a deep link at the Org tab, where the skill
 * never appears — a name-filtered landing list with no rows.
 */
export function skillTabForUser(
  summary: SkillSummary,
  userId: string,
): SkillListScope {
  const authoredOrSubscribed =
    summary.skill.created_by === userId || summary.subscription !== null;
  return authoredOrSubscribed ? "personal" : "org";
}
